'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { isOrderEligibleAccount } from '@/lib/utils/account-select'
import {
  cancelPendingAccount,
  createAccount,
  disableAccount,
  fetchUserAccounts,
  updateAccount,
} from '@/service/account.api'
import { AccountStatus } from '@/types/generated/common/account'
import {
  CreateAccountRequest,
  CreateAccountResponse,
  Success,
  UpdateAccountRequest,
} from '@/types/generated/public_api'
import { ErrorResponse } from '@/types/service'

// Query keys
export const accountKeys = {
  all: (userId?: string | null) => ['accounts', userId] as const,
  userAccounts: (userId?: string | null) => [...accountKeys.all(userId), 'user'] as const,
  balances: (userId?: string | null, accountId?: string) =>
    [...accountKeys.all(userId), 'balances', accountId] as const,
  allBalances: (userId?: string | null) => [...accountKeys.all(userId), 'balances', 'all'] as const,
}

// User accounts query
type UserAccountsData = Awaited<ReturnType<typeof fetchUserAccounts>>

// Module-level so their identity is stable: TanStack only reuses the cached
// selection while `select` is the same function; an inline closure would
// re-run the filter on every render of every observer.
export const selectAllAccounts = (data: UserAccountsData) => ({ accounts: data.accounts })
export const selectActiveAccounts = (data: UserAccountsData) => ({
  accounts: data.accounts.filter(
    (account) =>
      ![AccountStatus.ACCOUNT_STATUS_SUSPENDED, AccountStatus.ACCOUNT_STATUS_INACTIVE].includes(
        account.status
      )
  ),
})

export const useUserAccounts = (options?: {
  showDisabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean | 'always'
  refetchOnWindowFocus?: boolean | 'always'
  refetchInterval?: number | false
  enabled?: boolean
}) => {
  const { user } = useAuth()
  const showDisabled = options?.showDisabled ?? false
  return useQuery({
    queryKey: [...accountKeys.userAccounts(user?.userId), { showDisabled }],
    queryFn: () => fetchUserAccounts(),
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    refetchOnMount: options?.refetchOnMount,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    refetchInterval: options?.refetchInterval,
    select: showDisabled ? selectAllAccounts : selectActiveAccounts,
  })
}

/**
 * Hands back the previous array whenever the new one holds the same ids in the same
 * order, so the reference only changes when the membership actually does.
 *
 * React Query gives `data` a fresh object identity on every refetch — window refocus
 * once the 5-minute `staleTime` lapses, or any `invalidateQueries` from an account
 * mutation — so deriving ids with a plain `useMemo(..., [data?.accounts])` produces a
 * brand-new array even when not a single account changed. That array is a dependency
 * of WS payload memos and of row/context memos across ~30 call sites, so the churn
 * fans out well past this hook: the account-stream listener effect
 * (use-web-socket-with-proto.ts) tears down and re-registers its listener on it, for
 * one. The subscription itself was never at risk — that keys off a value-based
 * `payloadKey` (see `payloadDeps` in use-orders-stream.ts) — this just stops the
 * pointless downstream work around it.
 */
function useStableAccountIds(next: string[]): string[] {
  const idsRef = useRef(next)

  return useMemo(() => {
    const previous = idsRef.current
    if (previous.length === next.length && previous.every((id, i) => id === next[i])) {
      return previous
    }
    idsRef.current = next
    return next
  }, [next])
}

// Accounts strictly in ACCOUNT_STATUS_ACTIVE — the set to fan out per-account
// data subscriptions (positions, and later orders/trades) over, as opposed to
// useUserAccounts' broader default (excludes only suspended/inactive).
export const useActiveAccountIds = (): string[] => {
  const { data } = useUserAccounts()
  // Deliberately NOT sorted, unlike useActiveAccountsScope below — callers here take
  // the list in the server's own order.
  const accountIds = useMemo(
    () =>
      (data?.accounts ?? [])
        .filter((account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE)
        .map((account) => account.accountId),
    [data?.accounts]
  )
  return useStableAccountIds(accountIds)
}

// Accounts that can actually receive an order (ACTIVE and not read-only — see
// isOrderEligibleAccount). Use this, not useActiveAccountIds, to decide order
// targets; useActiveAccountIds/useActiveAccountsScope stay the viewing/subscription
// scope. `isResolved` separates "accounts still loading" from "loaded, none eligible",
// while `hasError` lets callers distinguish a failed query from both states.
export const useOrderEligibleAccounts = () => {
  const accountsQuery = useUserAccounts()
  const accounts = useMemo(
    () => (accountsQuery.data?.accounts ?? []).filter(isOrderEligibleAccount),
    [accountsQuery.data?.accounts]
  )
  // Sorted, same reasoning as useActiveAccountsScope: [a,b] and [b,a] must not be
  // distinct dependency keys for the same set.
  const derivedAccountIds = useMemo(
    () => accounts.map((account) => account.accountId).sort(),
    [accounts]
  )
  const accountIds = useStableAccountIds(derivedAccountIds)
  return useMemo(
    () => ({
      accounts,
      accountIds,
      isResolved: accountsQuery.isSuccess,
      hasError: accountsQuery.isError,
      hasAccounts: accounts.length > 0,
    }),
    [accounts, accountIds, accountsQuery.isError, accountsQuery.isSuccess]
  )
}

// The gating primitive for every batched `account_ids` request/subscription
// (see docs/superpowers/specs/2026-08-02-multi-account-request-scope-design.md).
// `accountIds: []` on the wire means "every account the caller can read," so
// callers must distinguish "account list still loading" from "loaded with
// zero active accounts" — `isResolved`/`hasAccounts` make that distinction
// explicit instead of every call site re-deriving it from `Boolean(accountId)`.
// Derives straight from useUserAccounts() rather than also calling
// useActiveAccountIds() (which wraps the same query) — one hook subscription
// instead of two for the same underlying data.
export const useActiveAccountsScope = () => {
  const accountsQuery = useUserAccounts()
  const derivedAccountIds = useMemo(
    () =>
      (accountsQuery.data?.accounts ?? [])
        .filter((account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE)
        .map((account) => account.accountId)
        .sort(),
    [accountsQuery.data?.accounts]
  )
  // Reference-stable across no-op refetches — see useStableAccountIds above.
  const accountIds = useStableAccountIds(derivedAccountIds)
  return useMemo(
    () => ({
      accountIds,
      // Always sorted above before joining — an unsorted join would make
      // [a,b] and [b,a] distinct cache/dep keys for the same scope.
      scopeKey: accountIds.join(','),
      isResolved: accountsQuery.isSuccess,
      hasAccounts: accountIds.length > 0,
    }),
    [accountIds, accountsQuery.isSuccess]
  )
}

export const useCreateAccount = (props?: {
  onSuccess?: (data: CreateAccountResponse, variables: CreateAccountRequest) => void
  onError?: (error: ErrorResponse, variables: CreateAccountRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: CreateAccountRequest) => createAccount(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useUpdateAccount = (props?: {
  onSuccess?: (data: Success, variables: UpdateAccountRequest) => void
  onError?: (error: ErrorResponse, variables: UpdateAccountRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: UpdateAccountRequest) => updateAccount(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useDisableAccount = (props?: {
  onSuccess?: (data: Success, variables: string) => void
  onError?: (error: ErrorResponse, variables: string) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (accountId: string) => disableAccount(accountId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

// Cancel an in-progress (never-activated) account setup. The backend
// re-verifies eligibility server-side and may hard-delete, soft-disable, or
// reject — the `message` on both success and error is the only signal, so
// callers should surface it verbatim rather than inferring an outcome.
export const useCancelPendingAccount = (props?: {
  onSuccess?: (data: Success, variables: string) => void
  onError?: (error: ErrorResponse, variables: string) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (accountId: string) => cancelPendingAccount(accountId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}
